import {
  Box,
  Tooltip,
} from '@mui/material';


// ===== STITCH DESIGN TOKENS =====
const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};


const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);


// Stitch shared styles
const stitchInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.5rem',
    backgroundColor: T.surfaceLow,
    fontFamily: T.fontBody,
    '&:hover': { backgroundColor: T.surfaceHigh },
    '&.Mui-focused': { backgroundColor: T.surface },
  },
};


const stitchPill = (color, bg) => ({
  fontFamily: T.fontBody,
  fontSize: '0.58rem',
  fontWeight: 700,
  color,
  bgcolor: bg,
  px: 1.25,
  py: 0.3,
  borderRadius: '9999px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  lineHeight: 1.4,
});


const stitchSectionLabel = {
  fontFamily: T.font,
  fontSize: '0.68rem',
  fontWeight: 700,
  color: T.outline,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};


const stitchGradientBtn = {
  background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
  color: '#fff',
  fontFamily: T.font,
  fontWeight: 600,
  fontSize: '0.82rem',
  border: 'none',
  borderRadius: '0.5rem',
  px: 2.5,
  py: 1,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  transition: 'all 0.15s ease',
  '&:hover': { opacity: 0.92, transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(82,96,117,0.25)' },
};


const stitchOutlineBtn = {
  fontFamily: T.font,
  fontWeight: 600,
  fontSize: '0.82rem',
  border: `1.5px solid ${T.outlineVariant}`,
  borderRadius: '0.5rem',
  color: T.primary,
  bgcolor: 'transparent',
  px: 2,
  py: 0.75,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  transition: 'all 0.15s ease',
  '&:hover': { borderColor: T.primary, bgcolor: T.surfaceLow },
};


const stitchCard = {
  bgcolor: T.surface,
  boxShadow: T.shadow,
  borderRadius: '0.75rem',
  transition: 'all 0.2s ease',
  '&:hover': { boxShadow: '0 8px 24px rgba(45,51,53,0.08)', transform: 'translateY(-2px)' },
};


const stitchDialogPaper = { borderRadius: '0.75rem' };


const stitchTooltipSlotProps = {
  tooltip: {
    sx: {
      fontFamily: T.fontBody,
      fontSize: '0.75rem',
      bgcolor: T.text,
      borderRadius: '0.5rem',
    },
  },
};


const stitchProgressSx = {
  height: 6,
  borderRadius: 3,
  backgroundColor: T.surfaceLow,
  '& .MuiLinearProgress-bar': {
    background: `linear-gradient(135deg, ${T.tertiary}, ${T.primary})`,
    borderRadius: 3,
  },
};


// Stitch skeleton pulse
const stitchSkeleton = (w, h) => ({
  width: w,
  height: h,
  bgcolor: T.surfaceLow,
  borderRadius: '0.5rem',
  '@keyframes stitchPulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 1 },
  },
  animation: 'stitchPulse 1.8s ease-in-out infinite',
});


// Stitch divider
const StitchDivider = () => <Box sx={{ height: '1px', bgcolor: T.surfaceLow }} />;


// Stitch icon button replacement
const StitchIconBtn = ({ onClick, children, disabled, sx = {}, title, ...rest }) => (
  <Tooltip title={title || ''} slotProps={stitchTooltipSlotProps}>
    <Box
      component="span"
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: '0.5rem',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        color: T.secondary,
        transition: 'all 0.15s ease',
        '&:hover': disabled ? {} : { bgcolor: T.surfaceLow, color: T.primary },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  </Tooltip>
);

export {
  T,
  Icon,
  stitchInputSx,
  stitchPill,
  stitchSectionLabel,
  stitchGradientBtn,
  stitchOutlineBtn,
  stitchCard,
  stitchDialogPaper,
  stitchTooltipSlotProps,
  stitchProgressSx,
  stitchSkeleton,
  StitchDivider,
  StitchIconBtn,
};
